import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TypographyMuted, TypographyXS } from '@/components/ui/typography'
import { useResearchAuditHistoryQuery } from '@/queries/useResearchAuditHistoryQuery'
import { History, Loader2, ShieldCheck, User, Calendar, Tag, ArrowRight } from 'lucide-react'
import { getTimeDate } from '@/utils/date'
import { Input } from '@/components/ui/input'
import { ResearchAuditItem } from '@/api/business/marketResearch'

interface ResearchAuditDialogProps {
  isOpen: boolean
  onClose: () => void
  skPesquisa: number
  skProdutosPesquisados?: number
}

const ResearchAuditDialog = ({
  isOpen,
  onClose,
  skPesquisa,
  skProdutosPesquisados,
}: ResearchAuditDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  const { data: auditHistory, isLoading } = useResearchAuditHistoryQuery(
    {
      SK_PESQUISA: skPesquisa,
      SK_PRODUTOS_PESQUISADOS: skProdutosPesquisados,
    },
    isOpen
  )

  const filteredHistory = (auditHistory || []).filter((item: ResearchAuditItem) => {
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    return (
      item.NM_USUARIO?.toLowerCase().includes(term) ||
      item.DS_PRODUTO?.toLowerCase().includes(term) ||
      item.DS_NOME_FANTASIA?.toLowerCase().includes(term) ||
      item.NM_CAMPO?.toLowerCase().includes(term) ||
      item.DS_JUSTIFICATIVA?.toLowerCase().includes(term)
    )
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[800px] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <History size={22} />
            <DialogTitle>Histórico de Auditoria de Correções</DialogTitle>
          </div>
          <div className="flex items-center justify-between mt-1">
            <TypographyMuted>
              Registro permanente e imutável de todas as alterações realizadas em pesquisas publicadas.
            </TypographyMuted>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
              <ShieldCheck size={14} /> Histórico Preservado
            </span>
          </div>
        </DialogHeader>

        <div className="my-2">
          <Input
            placeholder="Filtrar por usuário, produto, cliente, campo ou justificativa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full text-xs"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-sm text-gray-600">Carregando histórico de auditoria...</span>
          </div>
        ) : (
          <div className="flex-1 overflow-auto space-y-3 pr-1">
            {filteredHistory && filteredHistory.length > 0 ? (
              filteredHistory.map((log) => (
                <div
                  key={log.SK_AUDITORIA_PESQUISA}
                  className="p-3.5 border rounded-xl bg-white shadow-sm hover:border-gray-300 transition-all space-y-2"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-500 shrink-0" />
                      <span className="text-xs font-bold text-gray-900">
                        {log.NM_USUARIO || `Usuário #${log.SK_USUARIO}`}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Calendar size={13} />
                      <span>{log.DT_ALTERACAO ? getTimeDate(new Date(log.DT_ALTERACAO)) : 'N/A'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div>
                      <TypographyXS className="text-gray-400">PRODUTO / CLIENTE</TypographyXS>
                      <p className="font-semibold text-gray-800 truncate" title={log.DS_PRODUTO}>
                        {log.DS_PRODUTO || `Produto #${log.SK_PRODUTO}`}
                      </p>
                      <p className="text-gray-600 truncate" title={log.DS_NOME_FANTASIA}>
                        {log.DS_NOME_FANTASIA || `Cliente #${log.SK_CLIENTE}`}
                      </p>
                    </div>

                    <div>
                      <TypographyXS className="text-gray-400">ALTERAÇÃO REALIZADA</TypographyXS>
                      <div className="flex items-center gap-1.5 font-medium text-gray-900 mt-0.5">
                        <Tag size={13} className="text-primary" />
                        <span>{log.NM_CAMPO}:</span>
                        <span className="line-through text-red-600 font-mono">{log.VL_ANTERIOR}</span>
                        <ArrowRight size={12} className="text-gray-400" />
                        <span className="text-emerald-700 font-bold font-mono">{log.VL_NOVO}</span>
                      </div>
                    </div>
                  </div>

                  {log.DS_JUSTIFICATIVA && (
                    <div className="bg-amber-50/70 border border-amber-200/80 rounded-lg p-2 text-xs">
                      <span className="font-semibold text-amber-900">Justificativa: </span>
                      <span className="text-amber-800">{log.DS_JUSTIFICATIVA}</span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <History size={40} className="mb-3 opacity-40 text-gray-400" />
                <p className="text-base font-semibold text-gray-700">Nenhum registro de auditoria encontrado</p>
                <TypographyMuted className="text-xs text-center mt-1">
                  {searchTerm
                    ? 'Tente remover os filtros de busca'
                    : 'Nenhuma correção foi realizada nesta pesquisa até o momento.'}
                </TypographyMuted>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ResearchAuditDialog
